import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  FormHelperText,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CloudUpload,
  CheckCircle,
  Business,
  Description,
  Lock,
  Email,
  Phone,
  Badge,
  AttachFile,
  ArrowForward,
  ArrowBack,
  PictureAsPdf,
  Image as ImageIcon,
  Person,
  AccountBalance,
  Verified,
  Home,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { stateAssociationAPI } from '../api/stateAssociation.api';
import { useAuth } from '../context/AuthContext';

// File validations
const FILE_VALIDATIONS = {
  document: {
    accept: 'image/png,image/jpeg,image/jpg,application/pdf',
    maxSize: 5 * 1024 * 1024, // 5MB
    maxSizeLabel: '5MB',
    allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
    typeLabel: 'PNG, JPG, JPEG, PDF'
  },
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components (Same as athlete form)
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#ffffff',
  paddingBottom: theme.spacing(6),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  backgroundColor: '#ffffff',
  animation: `${fadeIn} 0.6s ease-out`,
  border: '1px solid #e0e0e0',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: '#e0e0e0',
    },
    '&:hover fieldset': {
      borderColor: '#291e6c',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#291e6c',
    },
  },
}));

const UploadBox = styled(Box)(({ theme, hasFile, error }) => ({
  border: `2px dashed ${error ? '#a70000' : hasFile ? '#4caf50' : '#e0e0e0'}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: hasFile ? 'rgba(76, 175, 80, 0.05)' : '#fafafa',
  '&:hover': {
    borderColor: '#291e6c',
    backgroundColor: 'rgba(41, 30, 108, 0.02)',
    transform: 'translateY(-2px)',
  },
}));

const FilePreviewCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(76, 175, 80, 0.08)',
  borderRadius: theme.spacing(1),
  border: `1px solid #4caf50`,
  animation: `${fadeIn} 0.4s ease-out`,
}));

const NavigationButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(1),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  '& .MuiStepConnector-line': {
    borderColor: '#e0e0e0',
    borderTopWidth: 3,
  },
  '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
    borderColor: '#291e6c',
  },
  '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
    borderColor: '#4caf50',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root': {
    color: '#e0e0e0',
    fontSize: '2rem',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root.Mui-active': {
    color: '#291e6c',
  },
  '& .MuiStepLabel-root .MuiStepIcon-root.Mui-completed': {
    color: '#4caf50',
  },
}));

const steps = ['Registration Details', 'Office Bearer Information', 'Account Setup'];

const StateAssociationRegistrationForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [filePreviewUrls, setFilePreviewUrls] = useState({});
  const [emailOtp, setEmailOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');

  const [formData, setFormData] = useState({
    // Registration Details
    registrationNumber: '',
    lastElectionDate: '',
    
    // President Details
    presidentFullName: '',
    presidentAddress: '',
    presidentEmail: '',
    presidentMobile: '',
    presidentPan: '',
    presidentAadharNumber: '',
    
    // Secretary Details
    secretaryFullName: '',
    secretaryAddress: '',
    secretaryEmail: '',
    secretaryMobile: '',
    secretaryPan: '',
    secretaryAadharNumber: '',
    
    // Treasurer Details
    treasurerFullName: '',
    treasurerAddress: '',
    treasurerEmail: '',
    treasurerMobile: '',
    treasurerPan: '',
    treasurerAadharNumber: '',
    
    // Login Information
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [files, setFiles] = useState({
    registrationCertificate: null,
    presidentPanCard: null,
    presidentAadharFront: null,
    presidentAadharBack: null,
    secretaryPanCard: null,
    secretaryAadharFront: null,
    secretaryAadharBack: null,
    treasurerPanCard: null,
    treasurerAadharFront: null,
    treasurerAadharBack: null,
  });

  const calculateProgress = () => {
    return (activeStep / steps.length) * 100;
  };

  const validateFile = (file) => {
    const validation = FILE_VALIDATIONS.document;
    
    if (file.size > validation.maxSize) {
      return `File size must be less than ${validation.maxSizeLabel}`;
    }
    
    if (!validation.allowedTypes.includes(file.type)) {
      return `Only ${validation.typeLabel} files are allowed`;
    }
    
    return null;
  };

  const sendOtp = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email to send OTP.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      await stateAssociationAPI.sendVerificationOTP(formData.email);
      toast.success('OTP sent to your email.');
      setOtpSent(true);
      setOtpVerified(false);
    } catch (error) {
      setOtpError(error.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!emailOtp) {
      setOtpError('Please enter the OTP.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      await stateAssociationAPI.verifyOTP({ email: formData.email, otp: emailOtp });
      toast.success('Email verified successfully.');
      setOtpVerified(true);
    } catch (error) {
      setOtpError(error.response?.data?.message || 'Invalid OTP.');
      setOtpVerified(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Registration Details
      if (!formData.registrationNumber || formData.registrationNumber.length < 3) {
        newErrors.registrationNumber = 'Registration number must be at least 3 characters';
      }

      if (!formData.lastElectionDate) {
        newErrors.lastElectionDate = 'Last election date is required';
      } else {
        const electionDate = new Date(formData.lastElectionDate);
        const today = new Date();
        if (electionDate > today) {
          newErrors.lastElectionDate = 'Last election date cannot be in the future';
        }
      }

      if (!files.registrationCertificate) {
        newErrors.registrationCertificate = 'Registration certificate is required';
      }
    }

    if (step === 1) {
      // President validation
      if (!formData.presidentFullName || formData.presidentFullName.length < 3) {
        newErrors.presidentFullName = 'President full name must be at least 3 characters';
      }
      if (!formData.presidentAddress || formData.presidentAddress.length < 10) {
        newErrors.presidentAddress = 'President address must be at least 10 characters';
      }
      if (!formData.presidentEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.presidentEmail)) {
        newErrors.presidentEmail = 'Valid president email is required';
      }
      if (!formData.presidentMobile || !/^[6-9]\d{9}$/.test(formData.presidentMobile)) {
        newErrors.presidentMobile = 'President mobile must be 10 digits starting with 6-9';
      }
      if (!formData.presidentPan || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.presidentPan.toUpperCase())) {
        newErrors.presidentPan = 'Invalid president PAN format (e.g., ABCDE1234F)';
      }
      if (!formData.presidentAadharNumber || !/^[2-9]\d{11}$/.test(formData.presidentAadharNumber)) {
        newErrors.presidentAadharNumber = 'President Aadhar must be 12 digits and cannot start with 0 or 1';
      }

      // Secretary validation
      if (!formData.secretaryFullName || formData.secretaryFullName.length < 3) {
        newErrors.secretaryFullName = 'Secretary full name must be at least 3 characters';
      }
      if (!formData.secretaryAddress || formData.secretaryAddress.length < 10) {
        newErrors.secretaryAddress = 'Secretary address must be at least 10 characters';
      }
      if (!formData.secretaryEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.secretaryEmail)) {
        newErrors.secretaryEmail = 'Valid secretary email is required';
      }
      if (formData.secretaryEmail === formData.presidentEmail) {
        newErrors.secretaryEmail = 'Secretary email must be different from President email';
      }
      if (!formData.secretaryMobile || !/^[6-9]\d{9}$/.test(formData.secretaryMobile)) {
        newErrors.secretaryMobile = 'Secretary mobile must be 10 digits starting with 6-9';
      }
      if (formData.secretaryMobile === formData.presidentMobile) {
        newErrors.secretaryMobile = 'Secretary mobile must be different from President mobile';
      }
      if (!formData.secretaryPan || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.secretaryPan.toUpperCase())) {
        newErrors.secretaryPan = 'Invalid secretary PAN format (e.g., ABCDE1234F)';
      }
      if (formData.secretaryPan.toUpperCase() === formData.presidentPan.toUpperCase()) {
        newErrors.secretaryPan = 'Secretary PAN must be different from President PAN';
      }
      if (!formData.secretaryAadharNumber || !/^[2-9]\d{11}$/.test(formData.secretaryAadharNumber)) {
        newErrors.secretaryAadharNumber = 'Secretary Aadhar must be 12 digits and cannot start with 0 or 1';
      }
      if (formData.secretaryAadharNumber === formData.presidentAadharNumber) {
        newErrors.secretaryAadharNumber = 'Secretary Aadhar must be different from President Aadhar';
      }

      // Treasurer validation
      if (!formData.treasurerFullName || formData.treasurerFullName.length < 3) {
        newErrors.treasurerFullName = 'Treasurer full name must be at least 3 characters';
      }
      if (!formData.treasurerAddress || formData.treasurerAddress.length < 10) {
        newErrors.treasurerAddress = 'Treasurer address must be at least 10 characters';
      }
      if (!formData.treasurerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.treasurerEmail)) {
        newErrors.treasurerEmail = 'Valid treasurer email is required';
      }
      if (formData.treasurerEmail === formData.presidentEmail || formData.treasurerEmail === formData.secretaryEmail) {
        newErrors.treasurerEmail = 'Treasurer email must be different from President and Secretary emails';
      }
      if (!formData.treasurerMobile || !/^[6-9]\d{9}$/.test(formData.treasurerMobile)) {
        newErrors.treasurerMobile = 'Treasurer mobile must be 10 digits starting with 6-9';
      }
      if (formData.treasurerMobile === formData.presidentMobile || formData.treasurerMobile === formData.secretaryMobile) {
        newErrors.treasurerMobile = 'Treasurer mobile must be different from President and Secretary mobiles';
      }
      if (!formData.treasurerPan || !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.treasurerPan.toUpperCase())) {
        newErrors.treasurerPan = 'Invalid treasurer PAN format (e.g., ABCDE1234F)';
      }
      if (formData.treasurerPan.toUpperCase() === formData.presidentPan.toUpperCase() || 
          formData.treasurerPan.toUpperCase() === formData.secretaryPan.toUpperCase()) {
        newErrors.treasurerPan = 'Treasurer PAN must be different from President and Secretary PANs';
      }
      if (!formData.treasurerAadharNumber || !/^[2-9]\d{11}$/.test(formData.treasurerAadharNumber)) {
        newErrors.treasurerAadharNumber = 'Treasurer Aadhar must be 12 digits and cannot start with 0 or 1';
      }
      if (formData.treasurerAadharNumber === formData.presidentAadharNumber || 
          formData.treasurerAadharNumber === formData.secretaryAadharNumber) {
        newErrors.treasurerAadharNumber = 'Treasurer Aadhar must be different from President and Secretary Aadhars';
      }

      // Document validation
      if (!files.presidentPanCard) {
        newErrors.presidentPanCard = 'President PAN card is required';
      }
      if (!files.presidentAadharFront) {
        newErrors.presidentAadharFront = 'President Aadhar front is required';
      }
      if (!files.presidentAadharBack) {
        newErrors.presidentAadharBack = 'President Aadhar back is required';
      }
      if (!files.secretaryPanCard) {
        newErrors.secretaryPanCard = 'Secretary PAN card is required';
      }
      if (!files.secretaryAadharFront) {
        newErrors.secretaryAadharFront = 'Secretary Aadhar front is required';
      }
      if (!files.secretaryAadharBack) {
        newErrors.secretaryAadharBack = 'Secretary Aadhar back is required';
      }
      if (!files.treasurerPanCard) {
        newErrors.treasurerPanCard = 'Treasurer PAN card is required';
      }
      if (!files.treasurerAadharFront) {
        newErrors.treasurerAadharFront = 'Treasurer Aadhar front is required';
      }
      if (!files.treasurerAadharBack) {
        newErrors.treasurerAadharBack = 'Treasurer Aadhar back is required';
      }
    }

    if (step === 2) {
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Valid email is required';
      }

      if (!otpVerified) {
        newErrors.otp = 'Please verify your email with OTP before submitting';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?^()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password)) {
        newErrors.password = 'Password must contain 1 uppercase, 1 lowercase, 1 number, and 1 symbol';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }

      if (!termsAccepted) {
        newErrors.terms = 'You must accept the terms and conditions';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    setFiles((prev) => ({ ...prev, [fieldName]: file }));
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setFilePreviewUrls((prev) => ({ ...prev, [fieldName]: url }));
    
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setLoading(true);
    try {
      const submitFormData = new FormData();
      
      // Append text fields
      Object.keys(formData).forEach((key) => {
        if (formData[key] && key !== 'confirmPassword') {
          submitFormData.append(key, formData[key]);
        }
      });

      // Append files
      Object.keys(files).forEach((key) => {
        if (files[key]) {
          submitFormData.append(key, files[key]);
        }
      });

      const response = await stateAssociationAPI.register(submitFormData);
      
      if (response.success) {
        toast.success('Registration successful! Awaiting admin verification.');
        
        // Store association ID and navigate to success page or dashboard
        localStorage.setItem('associationId', response.data.associationId);
        login(
          response.data,
          response.data.token || response.token,
          'stateAssociation'
        );
        setTimeout(() => {
          navigate('/state-association/success', { 
            state: { 
              associationData: response.data,
              registrationNumber: formData.registrationNumber
            } 
          });
        }, 1500);
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach((err) => {
          serverErrors[err.field] = err.message;
        });
        setErrors(serverErrors);
        toast.error('Please fix the validation errors');
      } else {
        toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFileUpload = (id, label, fieldName, required = true) => {
    const hasFile = files[fieldName];
    const hasError = errors[fieldName];
    const isPdf = hasFile && files[fieldName].type === 'application/pdf';

    return (
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#291e6c' }}>
          {label} {required && '*'}
        </Typography>
        
        <input
          accept={FILE_VALIDATIONS.document.accept}
          style={{ display: 'none' }}
          id={id}
          type="file"
          onChange={(e) => handleFileChange(e, fieldName)}
        />
        
        <label htmlFor={id}>
          <UploadBox hasFile={hasFile} error={hasError}>
            <CloudUpload sx={{ fontSize: 48, color: hasFile ? '#4caf50' : '#291e6c', mb: 1 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              {hasFile ? 'File Selected' : 'Click to upload or drag and drop'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {FILE_VALIDATIONS.document.typeLabel} (Max {FILE_VALIDATIONS.document.maxSizeLabel})
            </Typography>
          </UploadBox>
        </label>

        {hasError && (
          <FormHelperText error sx={{ mt: 1 }}>
            {hasError}
          </FormHelperText>
        )}

        {hasFile && (
          <FilePreviewCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isPdf ? (
                  <PictureAsPdf sx={{ fontSize: 40, color: '#d32f2f' }} />
                ) : (
                  <ImageIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {files[fieldName].name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(files[fieldName].size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              </Box>
              <CheckCircle sx={{ color: '#4caf50', fontSize: 32 }} />
            </Box>

            {filePreviewUrls[fieldName] && !isPdf && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={filePreviewUrls[fieldName]}
                  alt={label}
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            )}
          </FilePreviewCard>
        )}
      </Grid>
    );
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <SectionHeader>
              <Business sx={{ fontSize: 32, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
                  Registration Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Provide your state association registration information
                </Typography>
              </Box>
            </SectionHeader>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Registration Number"
                  name="registrationNumber"
                  value={formData.registrationNumber}
                  onChange={handleChange}
                  error={!!errors.registrationNumber}
                  helperText={errors.registrationNumber}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Badge sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Last Election Date"
                  name="lastElectionDate"
                  type="date"
                  value={formData.lastElectionDate}
                  onChange={handleChange}
                  error={!!errors.lastElectionDate}
                  helperText={errors.lastElectionDate}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalance sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {renderFileUpload(
                'registration-certificate',
                'Registration Certificate',
                'registrationCertificate',
                true
              )}
            </Grid>
          </>
        );

      case 1:
        return (
          <>
            <SectionHeader>
              <Person sx={{ fontSize: 32, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
                  Office Bearer Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All three office bearers must be different individuals
                </Typography>
              </Box>
            </SectionHeader>

            <Alert severity="warning" icon={<Verified />} sx={{ mb: 3, borderRadius: 1.5 }}>
              President, Secretary, and Treasurer must have unique email addresses, mobile numbers, PAN numbers, and Aadhar numbers.
            </Alert>

            {/* President Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#291e6c', mb: 2 }}>
                1. President Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="President Full Name"
                    name="presidentFullName"
                    value={formData.presidentFullName}
                    onChange={handleChange}
                    error={!!errors.presidentFullName}
                    helperText={errors.presidentFullName}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="President Address"
                    name="presidentAddress"
                    value={formData.presidentAddress}
                    onChange={handleChange}
                    error={!!errors.presidentAddress}
                    helperText={errors.presidentAddress}
                    required
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="President Email"
                    name="presidentEmail"
                    type="email"
                    value={formData.presidentEmail}
                    onChange={handleChange}
                    error={!!errors.presidentEmail}
                    helperText={errors.presidentEmail}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="President Mobile"
                    name="presidentMobile"
                    value={formData.presidentMobile}
                    onChange={handleChange}
                    error={!!errors.presidentMobile}
                    helperText={errors.presidentMobile}
                    required
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="President PAN"
                    name="presidentPan"
                    value={formData.presidentPan}
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                      handleChange(e);
                    }}
                    error={!!errors.presidentPan}
                    helperText={errors.presidentPan || 'Format: ABCDE1234F'}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="President Aadhar Number"
                    name="presidentAadharNumber"
                    value={formData.presidentAadharNumber}
                    onChange={handleChange}
                    error={!!errors.presidentAadharNumber}
                    helperText={errors.presidentAadharNumber}
                    required
                    inputProps={{ maxLength: 12 }}
                  />
                </Grid>

                {renderFileUpload('president-pan', 'President PAN Card', 'presidentPanCard', true)}
                {renderFileUpload('president-aadhar-front', 'President Aadhar (Front)', 'presidentAadharFront', true)}
                {renderFileUpload('president-aadhar-back', 'President Aadhar (Back)', 'presidentAadharBack', true)}
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Secretary Details */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#291e6c', mb: 2 }}>
                2. Secretary Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Secretary Full Name"
                    name="secretaryFullName"
                    value={formData.secretaryFullName}
                    onChange={handleChange}
                    error={!!errors.secretaryFullName}
                    helperText={errors.secretaryFullName}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Secretary Address"
                    name="secretaryAddress"
                    value={formData.secretaryAddress}
                    onChange={handleChange}
                    error={!!errors.secretaryAddress}
                    helperText={errors.secretaryAddress}
                    required
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Secretary Email"
                    name="secretaryEmail"
                    type="email"
                    value={formData.secretaryEmail}
                    onChange={handleChange}
                    error={!!errors.secretaryEmail}
                    helperText={errors.secretaryEmail}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Secretary Mobile"
                    name="secretaryMobile"
                    value={formData.secretaryMobile}
                    onChange={handleChange}
                    error={!!errors.secretaryMobile}
                    helperText={errors.secretaryMobile}
                    required
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Secretary PAN"
                    name="secretaryPan"
                    value={formData.secretaryPan}
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                      handleChange(e);
                    }}
                    error={!!errors.secretaryPan}
                    helperText={errors.secretaryPan || 'Format: ABCDE1234F'}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Secretary Aadhar Number"
                    name="secretaryAadharNumber"
                    value={formData.secretaryAadharNumber}
                    onChange={handleChange}
                    error={!!errors.secretaryAadharNumber}
                    helperText={errors.secretaryAadharNumber}
                    required
                    inputProps={{ maxLength: 12 }}
                  />
                </Grid>

                {renderFileUpload('secretary-pan', 'Secretary PAN Card', 'secretaryPanCard', true)}
                {renderFileUpload('secretary-aadhar-front', 'Secretary Aadhar (Front)', 'secretaryAadharFront', true)}
                {renderFileUpload('secretary-aadhar-back', 'Secretary Aadhar (Back)', 'secretaryAadharBack', true)}
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Treasurer Details */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#291e6c', mb: 2 }}>
                3. Treasurer Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer Full Name"
                    name="treasurerFullName"
                    value={formData.treasurerFullName}
                    onChange={handleChange}
                    error={!!errors.treasurerFullName}
                    helperText={errors.treasurerFullName}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer Address"
                    name="treasurerAddress"
                    value={formData.treasurerAddress}
                    onChange={handleChange}
                    error={!!errors.treasurerAddress}
                    helperText={errors.treasurerAddress}
                    required
                    multiline
                    rows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Home sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer Email"
                    name="treasurerEmail"
                    type="email"
                    value={formData.treasurerEmail}
                    onChange={handleChange}
                    error={!!errors.treasurerEmail}
                    helperText={errors.treasurerEmail}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer Mobile"
                    name="treasurerMobile"
                    value={formData.treasurerMobile}
                    onChange={handleChange}
                    error={!!errors.treasurerMobile}
                    helperText={errors.treasurerMobile}
                    required
                    inputProps={{ maxLength: 10 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: '#291e6c' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer PAN"
                    name="treasurerPan"
                    value={formData.treasurerPan}
                    onChange={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                      handleChange(e);
                    }}
                    error={!!errors.treasurerPan}
                    helperText={errors.treasurerPan || 'Format: ABCDE1234F'}
                    required
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledTextField
                    fullWidth
                    label="Treasurer Aadhar Number"
                    name="treasurerAadharNumber"
                    value={formData.treasurerAadharNumber}
                    onChange={handleChange}
                    error={!!errors.treasurerAadharNumber}
                    helperText={errors.treasurerAadharNumber}
                    required
                    inputProps={{ maxLength: 12 }}
                  />
                </Grid>

                {renderFileUpload('treasurer-pan', 'Treasurer PAN Card', 'treasurerPanCard', true)}
                {renderFileUpload('treasurer-aadhar-front', 'Treasurer Aadhar (Front)', 'treasurerAadharFront', true)}
                {renderFileUpload('treasurer-aadhar-back', 'Treasurer Aadhar (Back)', 'treasurerAadharBack', true)}
              </Grid>
            </Box>
          </>
        );

      case 2:
        return (
          <>
            <SectionHeader>
              <Lock sx={{ fontSize: 32, color: '#291e6c' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#291e6c' }}>
                  Account Setup
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your login credentials
                </Typography>
              </Box>
            </SectionHeader>

            <Grid container spacing={3}>
              {/* Email with OTP Verification */}
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email || 'This will be your login email'}
                  required
                  disabled={otpVerified}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                    endAdornment: otpVerified && (
                      <InputAdornment position="end">
                        <CheckCircle sx={{ color: '#4caf50' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* OTP Section */}
              {!otpVerified && (
                <Grid item xs={12}>
                  <Card sx={{ p: 3, backgroundColor: '#f8f7fc', border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Email Verification Required
                    </Typography>
                    
                    {!otpSent ? (
                      <NavigationButton
                        variant="outlined"
                        onClick={sendOtp}
                        disabled={otpLoading || !formData.email}
                        startIcon={<Email />}
                        sx={{ borderColor: '#291e6c', color: '#291e6c' }}
                      >
                        {otpLoading ? <CircularProgress size={24} /> : 'Send OTP'}
                      </NavigationButton>
                    ) : (
                      <Box>
                        <StyledTextField
                          fullWidth
                          label="Enter OTP"
                          value={emailOtp}
                          onChange={(e) => {
                            setEmailOtp(e.target.value);
                            setOtpError('');
                          }}
                          error={!!otpError}
                          helperText={otpError || 'OTP sent to your email'}
                          sx={{ mb: 2 }}
                        />
                        <Stack direction="row" spacing={2}>
                          <NavigationButton
                            variant="contained"
                            onClick={verifyOtp}
                            disabled={otpLoading || !emailOtp}
                            sx={{ backgroundColor: '#291e6c' }}
                          >
                            {otpLoading ? <CircularProgress size={24} /> : 'Verify OTP'}
                          </NavigationButton>
                          <NavigationButton
                            variant="outlined"
                            onClick={sendOtp}
                            disabled={otpLoading}
                            sx={{ borderColor: '#291e6c', color: '#291e6c' }}
                          >
                            Resend OTP
                          </NavigationButton>
                        </Stack>
                      </Box>
                    )}
                  </Card>
                </Grid>
              )}

              {otpVerified && (
                <Grid item xs={12}>
                  <Alert severity="success" icon={<CheckCircle />} sx={{ borderRadius: 1.5 }}>
                    Email verified successfully! You can now set your password.
                  </Alert>
                </Grid>
              )}

              {/* Password Fields */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password || 'Minimum 8 characters with uppercase, lowercase, number, and symbol'}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#291e6c' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Terms and Conditions */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => {
                        setTermsAccepted(e.target.checked);
                        if (errors.terms) {
                          setErrors((prev) => ({ ...prev, terms: '' }));
                        }
                      }}
                      sx={{
                        color: errors.terms ? '#d32f2f' : '#291e6c',
                        '&.Mui-checked': {
                          color: '#291e6c',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I hereby declare that the information provided is true and correct, and I accept all{' '}
                      <Box component="span" sx={{ color: '#291e6c', fontWeight: 600, cursor: 'pointer' }}>
                        terms and conditions
                      </Box>
                    </Typography>
                  }
                />
                {errors.terms && (
                  <FormHelperText error>{errors.terms}</FormHelperText>
                )}
              </Grid>
            </Grid>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
              mb: 1,
            }}
          >
            STATE ASSOCIATION REGISTRATION
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Register your state association with IWLF
          </Typography>
        </Box>

        {/* Progress Bar */}
        {/* <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, backgroundColor: 'white', py: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Container maxWidth="lg">
            <LinearProgress
              variant="determinate"
              value={calculateProgress()}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#291e6c',
                  borderRadius: 4,
                },
              }}
            />
          </Container>
        </Box> */}

        <StyledPaper>
          {/* Stepper */}
          <StyledStepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </StyledStepper>

          <Divider sx={{ my: 4 }} />

          {/* Step Content */}
          <Box sx={{ minHeight: '400px' }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <NavigationButton
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
              startIcon={<ArrowBack />}
              sx={{
                visibility: activeStep === 0 ? 'hidden' : 'visible',
                borderColor: '#291e6c',
                color: '#291e6c',
              }}
              variant="outlined"
            >
              Back
            </NavigationButton>

            {activeStep === steps.length - 1 ? (
              <NavigationButton
                onClick={handleSubmit}
                disabled={loading}
                endIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                sx={{
                  backgroundColor: '#291e6c',
                  color: 'white',
                  '&:hover': { backgroundColor: '#1f1750' },
                }}
              >
                {loading ? 'Submitting...' : 'Submit Registration'}
              </NavigationButton>
            ) : (
              <NavigationButton
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{
                  backgroundColor: '#291e6c',
                  color: 'white',
                  '&:hover': { backgroundColor: '#1f1750' },
                }}
              >
                Next
              </NavigationButton>
            )}
          </Box>
        </StyledPaper>
      </Container>
    </PageWrapper>
  );
};

export default StateAssociationRegistrationForm;
